import type { Chord } from '@tunasong/music-lib'
import {
  ChordLib,
  ScaleLib,
  analyzeChord,
  computeHarmonicTension,
  detectModalSignature,
  isChordVariant,
  isNumeralChordFunction,
} from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import cn from 'classnames'
import type { CSSProperties, FC, MouseEvent } from 'react'
import React from 'react'
import { ChordDegree } from '../note/index.js'
import { useTheoryCopy, useTheoryLevel } from '../theory-copy/use-theory-copy.js'
import { chordCheckCopy } from './chord-check-copy.js'
import { getChordConfidenceClassName } from './chord-confidence.js'
import { getChordCues } from './chord-cues.js'
import styles from './simple-chord.module.css'
import { TensionStripe } from './tension-stripe-bar.js'
import { buildTensionAriaPhrase } from './tension-stripe.js'

/**
 * Suffix appended to the chip's aria-label when the chord carries a modal
 * characteristic note. Mirrors Joni's UX spec §2.3: "<chord name>, modal —
 * Dorian signature". Capitalises the mode name; the noun "signature" is the
 * level-independent identifier (the lexicon `tagLabel` carries level-specific
 * variants used in the WindowTag chip, not here).
 */
const buildModalAriaSuffix = (mode: string): string => {
  const capitalised = mode.charAt(0).toUpperCase() + mode.slice(1)
  return `modal — ${capitalised} signature`
}

export interface SimpleChordProps {
  chord: Chord
  /** Transpose the display by a number of semitones ( + / - ) */
  transpose?: number
  /** Display transposition as part of the chord (+/-)transposition */
  showTranspose?: boolean
  scale?: Scale | null
  selected?: boolean
  focused?: boolean
  className?: string
  style?: CSSProperties
  /**
   * Show the cover-song "check this chord" ring and say so in the accessible name (Joni's chord-check
   * UX §1, §5). The caller decides: it holds the `checkHint`, the fingerprint and the view rules.
   */
  worthChecking?: boolean
  onClick?(chord: Chord): void
}

export const SimpleChord: FC<SimpleChordProps> = props => {
  const {
    chord,
    scale,
    showTranspose: _showTranspose = false,
    transpose = 0,
    selected = false,
    focused = false,
    className,
    style,
    onClick,
    worthChecking = false,
  } = props

  /** Moved by the key interval when the key is known, so Ab in C up 7 reads Eb, not D# (GH #2982) */
  const chordName = `${ChordLib.getName(ChordLib.transposeInKey(chord, transpose, scale))}${ChordLib.transposeLabel(transpose)}`

  /**
   * Use the theory analyzer to compute the chord's function relative to the
   * established scale. analyzeChord covers diatonic, parallel-mode borrowings,
   * secondary dominants, and chromatic outliers — so the superscript matches
   * what the InlineCaption / Caption / Sidebar surfaces show. We don't know
   * that the root is in the custom chord; analyzeChord returns null for those.
   */
  const analysis = isChordVariant(chord) && scale ? analyzeChord({ chord, scale }) : null

  /**
   * In key = diatonic to the key, read from the analysis rather than raw scale
   * membership, so the minor key's V, V7 and vii° (harmonic-minor lift) and
   * open chords (sus, power) count as in key. Borrowed chords, secondary
   * dominants and chromatic outliers are out of key. Chords the analyzer cannot
   * read (note lists) fall back to scale membership.
   */
  const inKey = analysis
    ? analysis.borrowing === null && analysis.chordFunction !== 'chromatic'
    : scale
      ? ScaleLib.inScale(scale, chord)
      : true

  /**
   * Per-chord S3a (modal characteristic-note glow). The detector returns an
   * array — one entry per qualifying chord — and we call it with a single-chord
   * window because the per-chord chip only needs to know about itself. The
   * helper is O(notes-per-chord) and runs once per render; React Compiler
   * memoizes the chip subtree so this stays cheap.
   */
  const theoryLevel = useTheoryLevel()
  const cues = getChordCues(theoryLevel)
  const modalHit = cues.modal && scale ? (detectModalSignature([chord], scale)[0] ?? null) : null

  /**
   * Tension Contour Stripe (Track A). Reuse the chip's existing `analysis` —
   * we do NOT re-run analyzeChord. No `next` chord is cheaply known at the chip
   * level, so tritone-sub detection that needs a resolution simply doesn't fire
   * here (acceptable for v1; the popup row carries the same per-chord reading).
   * The stripe paints only at the Advanced cue level AND when an analysis exists.
   */
  const tension = analysis ? computeHarmonicTension({ chord, analysis, scale: scale ?? undefined }) : null
  const showTension = cues.tension && tension !== null
  // Hook order stability: always call useTheoryCopy; empty id resolves to null.
  const tensionCopy = useTheoryCopy(tension ? `tension-${tension.bucket}` : '')

  const handleClick = onClick
    ? (ev: MouseEvent) => {
        ev.preventDefault()
        onClick(chord)
      }
    : undefined

  /* Screen-reader hint: out-of-key chords carry a visual signal (color + dotted
   * underline). The aria-label suffix ensures keyboard / SR users get the same
   * information without relying on the visible styling. When the chord ALSO
   * carries a modal signature, append the Joni §2.3 suffix so SR users hear
   * the modal context without needing to open the popup.
   */
  const ariaLabelParts: string[] = []
  if (cues.outOfKey && !inKey) {
    ariaLabelParts.push(`${chordName} (outside the key)`)
  }
  if (modalHit) {
    ariaLabelParts.push(`${chordName}, ${buildModalAriaSuffix(modalHit.mode)}`)
  }
  /* Mirror the stripe into the chip's aria-label so SR users hear the concept
   * without opening the card (visual spec §8). Low-confidence uses the inline
   * "uncertain" hedge (Bowie: UI-status microcopy, not lexicon copy); confident
   * uses the bucket's level-resolved tagLabel. Silent when level is off
   * (tensionCopy is null) so the gate is honoured on the spoken channel too. */
  if (showTension && tension && tensionCopy) {
    ariaLabelParts.push(buildTensionAriaPhrase(tensionCopy.tag, tension.lowConfidence === true))
  }
  /* The marker reads as one more part, after "(outside the key)" when both apply (Joni §5):
   * "F (outside the key). Worth a second listen." */
  if (worthChecking) {
    if (ariaLabelParts.length === 0) {
      ariaLabelParts.push(chordName)
    }
    ariaLabelParts.push(chordCheckCopy.chipSuffix)
  }
  const ariaLabel = ariaLabelParts.length > 0 ? ariaLabelParts.join('. ') : undefined

  return (
    <span
      contentEditable={false}
      onClick={handleClick}
      aria-label={ariaLabel}
      data-modal-signature={modalHit?.mode}
      className={cn(
        styles.chord,
        !cues.outOfKey ? styles['chord--plain'] : inKey ? styles['chord--inScale'] : styles['chord--outOfScale'],
        selected && focused && styles['chord--selected'],
        onClick && styles['chord--clickable'],
        modalHit && styles['chord--modal'],
        className
      )}
      style={style}
    >
      {chordName}
      {cues.numeral && analysis && isNumeralChordFunction(analysis.chordFunction) ? (
        <ChordDegree
          chordFunction={analysis.chordFunction}
          className={getChordConfidenceClassName(analysis.confidence)}
        />
      ) : null}
      {showTension && tension ? (
        <TensionStripe bucket={tension.bucket} lowConfidence={tension.lowConfidence === true} />
      ) : null}
      {worthChecking ? <span className={styles.checkRing} aria-hidden="true" data-chord-check-ring /> : null}
    </span>
  )
}
